import { Fragment, useState } from "react";
import PropTypes from "prop-types";
import LabelForm from "pages/signup/form/labelForm";
import { useTranslation } from "react-i18next";
import Select from "components/select";
import Input from "components/input";
import RadioButtons from "components/radioButtons";
import CreatableMultiSelect from "components/creatableMultiSelect";
import { domainOtherOption, catToolsOptions } from "utils/constantValues";

const MAX_DOMAINS = 8;

function Experience({
  data = {},
  watch = () => {},
  setValue = () => {},
  errors = {},
  register = () => {},
}) {
  const [t] = useTranslation();
  const isWillingToTravel = watch("isWillingToTravel");
  const isOpenForSecondment = watch("isOpenForSecondment");
  const [radios, setRadios] = useState([
    {
      title: t("Yes"),
      isActive: true,
    },
    { title: t("No"), isActive: false },
  ]);
  const [secondmentRadios, setSecondmentRadios] = useState([
    {
      title: t("Yes"),
      isActive: false,
    },
    { title: t("No"), isActive: true },
  ]);

  const [catToolsRadios, setCatToolsRadios] = useState([
    { title: t("Yes"), isActive: false },
    { title: t("No"), isActive: true },
  ]);

  const hasCatToolsExperience = watch("hasCatToolsExperience");

  const { domains = [], subDomains = [] } = data;

  const subDomainsOptions = subDomains.map((elem) => ({
    ...elem,
    label: `${elem.domainTitle} - ${elem.title}`,
    value: elem.id,
  }));

  const domainsOptions = domains.map((elem) => ({
    ...elem,
    label: elem.title,
    value: elem.id,
  }));

  const domainOthersId = domainsOptions.find(
    (elem) => elem.title === domainOtherOption
  )?.id;

  const maxDomainsErrorMessage = t("maxLengthDomainsError", {
    max: MAX_DOMAINS,
  });

  return (
    <Fragment>
      <LabelForm
        label={t("experienceTitle")}
        labelClassName="text-primary font-bold text-[24px] md-max:text-[18px] not-italic pt-[16px] bg-white z-[3]"
      />

      <div className="grid grid-cols-2 md-max:grid-cols-1 gap-[16px] max-w-[843px] sm-max:max-w-full pt-[10px] px-[0px] pb-[30px]  z-[3] bg-white border-b-[1px] border-solid border-default-color">
        <input type="hidden" {...register("isWillingToTravel")} />
        <input type="hidden" {...register("isOpenForSecondment")} />

        <Select
          label={t("domains")}
          placeholder={t("placeholderSelect", {
            field: t("domains").toLowerCase(),
          })}
          name={"domains"}
          closeMenuOnSelect={false}
          isMulti={true}
          value={
            watch("domains")
              ? domainsOptions.filter((elem) =>
                  watch("domains").includes(elem.id)
                )
              : null
          }
          isRequired={watch("domains") && watch("domains").length > MAX_DOMAINS}
          registerArgs={{
            require:
              watch("domains") && watch("domains").length > MAX_DOMAINS
                ? maxDomainsErrorMessage
                : false,
            validate: (value) => {
              if (value && value.length > MAX_DOMAINS) {
                return maxDomainsErrorMessage;
              }
              return true;
            },
          }}
          errorMessage={
            watch("domains") && watch("domains").length > MAX_DOMAINS
              ? maxDomainsErrorMessage
              : ""
          }
          showError={watch("domains") && watch("domains").length > MAX_DOMAINS}
          onChange={(e) => {
            setValue(
              "domains",
              e.map((e) => e.value)
            );

            setValue("subDomains", []);
          }}
          register={register}
          options={domainsOptions}
        />
        <Select
          label={t("subDomains")}
          placeholder={t("placeholderSelect", {
            field: t("subDomains").toLowerCase(),
          })}
          closeMenuOnSelect={false}
          isMulti={true}
          name={"subDomains"}
          isDisabled={subDomains.length <= 0 || watch("domains")?.length <= 0}
          value={
            watch("subDomains") && watch("subDomains")?.length > 0
              ? subDomainsOptions.filter((elem) =>
                  watch("subDomains").includes(elem.id)
                )
              : null
          }
          onChange={(e) => {
            setValue(
              "subDomains",
              e.map((e) => e.value)
            );
          }}
          register={register}
          options={subDomainsOptions}
        />
        {watch("domains") && watch("domains").includes(domainOthersId) && (
          <Input
            label={t("others")}
            placeholder={t("placeHolderType", {
              title: t("others").toLowerCase(),
            })}
            type="textarea"
            isRequired={watch("domains")?.includes(domainOthersId)}
            name="customDomain"
            errorMessage={
              errors.customDomain ? errors.customDomain.message : ""
            }
            register={register}
            registerArgs={{
              required: watch("domains")?.includes(domainOthersId)
                ? t("errorField", { field: t("others") })
                : false,
              validate: (value) => {
                return value.length > 128
                  ? t("maxCharacters", {
                      field: t("others"),
                      number: 128,
                    })
                  : null;
              },
            }}
            labelClassName="text-primary font-semibold text-[16px]"
            inputClassName={`flex h-[43px] w-full items-center gap-[8px] self-stretch rounded-[6px] border-[1px] border-solid ${
              errors.customDomain ? "border-red" : "border-default-color"
            } bg-transparent outline-none text-dark-gray font-regular text-[16px] not-italic  placeholder:font-regular placeholder:text-[16px] placeholder:not-italic placeholder:font-normal py-[16px] px-[14px]`}
          />
        )}

        <Input
          label={t("yearsOfExperienceTitle")}
          placeholder={t("placeHolderType", {
            title: t("yearsOfExperienceTitle").toLowerCase(),
          })}
          type="number"
          inputMode="numeric"
          name="yearsOfExperience"
          register={register}
          labelClassName="text-primary font-semibold text-[16px]"
          inputClassName="flex h-[43px] w-full items-center gap-[8px] self-stretch rounded-[6px] border-[1px] border-solid border-default-color bg-transparent outline-none text-dark-gray font-regular text-[16px] not-italic  placeholder:font-regular placeholder:text-[16px] placeholder:not-italic placeholder:font-normal py-[16px] px-[14px] "
        />
        <RadioButtons
          label={t("willingToTravel")}
          contents={radios}
          onClick={(_, index) => {
            setValue("isWillingToTravel", !isWillingToTravel);

            const updatedContents = radios.map((elem, i) => {
              return { ...elem, isActive: i === index };
            });

            setRadios(updatedContents);
          }}
        />
        <RadioButtons
          label={t("openForSecondment")}
          contents={secondmentRadios}
          onClick={(_, index) => {
            setValue("isOpenForSecondment", !isOpenForSecondment);

            const updatedContents = secondmentRadios.map((elem, i) => {
              return { ...elem, isActive: i === index };
            });

            setSecondmentRadios(updatedContents);
          }}
        />

        <RadioButtons
          label={t("catToolsExperience")}
          contents={catToolsRadios}
          onClick={(_, index) => {
            const newValue = !hasCatToolsExperience;
            setValue("hasCatToolsExperience", newValue);
            if (!newValue) {
              setValue("catTools", []);
            }
            const updatedContents = catToolsRadios.map((elem, i) => {
              return { ...elem, isActive: i === index };
            });
            setCatToolsRadios(updatedContents);
          }}
        />

        {hasCatToolsExperience && (
          <CreatableMultiSelect
            label={t("catToolsLabel")}
            placeholder={t("catToolsPlaceholder")}
            name="catTools"
            options={catToolsOptions}
            value={
              watch("catTools") && watch("catTools").length > 0
                ? watch("catTools").map((tool) =>
                    typeof tool === "string"
                      ? { label: tool, value: tool }
                      : tool
                  )
                : null
            }
            onChange={(selected) => {
              setValue(
                "catTools",
                selected.map((item) => item.value)
              );
            }}
            register={register}
            labelClassName="text-primary font-semibold text-[16px]"
          />
        )}
      </div>
    </Fragment>
  );
}

Experience.propTypes = {
  label: PropTypes.string,
  register: PropTypes.func,
  setValue: PropTypes.func,
  errors: PropTypes.object,
  watch: PropTypes.func,
  data: PropTypes.object,
  getValues: PropTypes.func,
};

export default Experience;
